import { useParams, Link } from "react-router-dom";
import {
  ChevronLeft,
  Clock,
  Loader2,
  FileText,
  CheckCircle2,
  PlayCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
// import { PowerPointViewer } from "pptx-react-viewer";
import {
  fileUrl,
  isEmbedVideo,
  loomEmbedsToHtml,
  resolveVideoUrl,
} from "../lib/utils";
import {
  COURSE_DETAIL,
  COURSE_WATCH_PROGRESS,
  CUSTOMER_COURSE_TEST_ATTEMPTS,
} from "@/lib/graphql/queries/course";
import {
  LOG_WATCH_EVENT,
  LOG_WATCH_PROGRESS,
  MARK_VIDEO_ENDED,
} from "@/lib/graphql/mutations/course";
import CourseTestDialog from "@/components/CourseTestDialog";

const apiUrl = import.meta.env.VITE_API_URL;

const PROGRESS_THROTTLE_MS = 5000;
const ENDED_THRESHOLD = 0.97; // 97% считается "видео закончилось"

const VideoLessonDetail = () => {
  const { id } = useParams<{ id: string }>();

  const { data, loading, error } = useQuery(COURSE_DETAIL, {
    variables: { courseId: id },
    skip: !id,
  });

  const { data: progressData } = useQuery(COURSE_WATCH_PROGRESS, {
    variables: { courseId: id },
    skip: !id,
  });

  const { data: attemptsData, refetch: refetchAttempts } = useQuery(
    CUSTOMER_COURSE_TEST_ATTEMPTS,
    {
      variables: { courseId: id },
      skip: !id,
    }
  );

  const lesson = data?.course;
  const test = lesson?.test;
  // External URLs and Loom share content (Loom etc.) are rendered as an embed
  // iframe instead of an HTML5 <video>, so native play/seek/ended events are
  // not available.
  const isEmbed = isEmbedVideo(lesson?.videoUrl);
  const alreadyPassed = !!attemptsData?.customerCourseTestAttempts?.some(
    (a: any) => a.passed
  );

  const [logProgress] = useMutation(LOG_WATCH_PROGRESS);
  const [logEvent] = useMutation(LOG_WATCH_EVENT);
  const [markEnded] = useMutation(MARK_VIDEO_ENDED);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const lastProgressLogRef = useRef<number>(0);
  const seekedFromSavedRef = useRef<boolean>(false);
  const endedHandledRef = useRef<boolean>(false);

  const [seekReady, setSeekReady] = useState(false);
  const [watchedPercent, setWatchedPercent] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  // Fetch the PPTX file as ArrayBuffer and convert to Uint8Array
  const [pptxContent, setPptxContent] = useState<Uint8Array | null>(null);
  const [pptxError, setPptxError] = useState<string | null>(null);
  const [pptxLoading, setPptxLoading] = useState(false);

  useEffect(() => {
    if (!lesson?.presentationFile) {
      setPptxContent(null);
      setPptxError(null);
      setPptxLoading(false);
      return;
    }

    let cancelled = false;
    setPptxLoading(true);
    setPptxError(null);

    fetch(
      `${apiUrl}/read-file?key=${encodeURIComponent(
        lesson.presentationFile
      )}&inline=true`
    )
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch: ${res.status} ${res.statusText}`);
        }
        return res.arrayBuffer();
      })
      .then((buffer) => {
        if (cancelled) return;
        setPptxContent(new Uint8Array(buffer));
        setPptxLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("PPTX fetch error:", err);
        setPptxError(err?.message || "Failed to load presentation");
        setPptxLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lesson?.presentationFile]);

  // Hydrate progress UI from server data
  useEffect(() => {
    const p = progressData?.courseWatchProgress;
    if (p) {
      setWatchedPercent(p.percent || 0);
      setCompleted(!!p.completed);
    }
  }, [progressData?.courseWatchProgress?.id]);

  const onLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video || seekedFromSavedRef.current) return;
    const saved = progressData?.courseWatchProgress?.position || 0;
    const dur = video.duration || 0;
    // Only seek if there's something meaningful to resume and the user
    // hasn't already finished.
    if (
      saved > 1 &&
      (!progressData?.courseWatchProgress?.completed) &&
      dur > 0 &&
      saved < dur - 5
    ) {
      try {
        video.currentTime = saved;
      } catch (e) {
        // ignore
      }
    }
    seekedFromSavedRef.current = true;
    setSeekReady(true);
  }, [progressData?.courseWatchProgress]);

  const sendProgress = useCallback(
    (force = false) => {
      const video = videoRef.current;
      if (!video || !id) return;
      const now = Date.now();
      if (!force && now - lastProgressLogRef.current < PROGRESS_THROTTLE_MS) {
        return;
      }
      lastProgressLogRef.current = now;
      const pos = video.currentTime || 0;
      const dur = video.duration || 0;
      const pct = dur > 0 ? (pos / dur) * 100 : 0;
      setWatchedPercent(pct);
      logProgress({
        variables: { courseId: id, position: pos, duration: dur, percent: pct },
      }).catch((err) => {
        console.error("logProgress error", err);
      });
    },
    [id, logProgress]
  );

  const onTimeUpdate = useCallback(() => {
    sendProgress(false);
  }, [sendProgress]);

  const onPlay = useCallback(() => {
    if (!id) return;
    logEvent({
      variables: { courseId: id, type: "PLAY", position: videoRef.current?.currentTime || 0 },
    }).catch(() => {});
  }, [id, logEvent]);

  const onPause = useCallback(() => {
    if (!id) return;
    sendProgress(true);
    logEvent({
      variables: { courseId: id, type: "PAUSE", position: videoRef.current?.currentTime || 0 },
    }).catch(() => {});
  }, [id, logEvent, sendProgress]);

  const onSeeking = useCallback(() => {
    if (!id) return;
    logEvent({
      variables: {
        courseId: id,
        type: "SEEK",
        position: videoRef.current?.currentTime || 0,
      },
    }).catch(() => {});
  }, [id, logEvent]);

  const handleEnded = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !id || endedHandledRef.current) return;
    endedHandledRef.current = true;

    const dur = video.duration || 0;
    const pos = video.currentTime || dur;

    try {
      await markEnded({
        variables: { courseId: id, position: pos, duration: dur },
      });
    } catch (err) {
      console.error("markEnded error", err);
    }

    setWatchedPercent(100);
    setCompleted(alreadyPassed || !test?.required);

    // Open test modal automatically if required and not already passed
    if (test?.required && !alreadyPassed) {
      setTestOpen(true);
    }
  }, [id, markEnded, test?.required, alreadyPassed]);

  const onTestCompleted = useCallback(() => {
    setCompleted(true);
    refetchAttempts();
  }, [refetchAttempts]);

  // Embeds (iframe) can't emit HTML5 video events, so the customer marks the
  // lesson as watched manually; this mirrors the semantics of handleEnded.
  const handleMarkEmbeddedWatched = useCallback(async () => {
    if (!id || endedHandledRef.current) return;
    endedHandledRef.current = true;
    try {
      await markEnded({
        variables: {
          courseId: id,
          position: 0,
          duration: typeof lesson?.duration === "number" ? lesson.duration : 0,
        },
      });
    } catch (err) {
      console.error("markEnded error", err);
    }
    setWatchedPercent(100);
    setCompleted(alreadyPassed || !test?.required);
    // Open test modal automatically if required and not already passed
    if (test?.required && !alreadyPassed) {
      setTestOpen(true);
    }
  }, [id, markEnded, test?.required, alreadyPassed, lesson?.duration]);

  // Cleanup
  useEffect(() => {
    const videoAtMount = videoRef.current;
    return () => {
      // Final progress flush on unmount
      if (videoAtMount && id) {
        const pos = videoAtMount.currentTime || 0;
        const dur = videoAtMount.duration || 0;
        const pct = dur > 0 ? (pos / dur) * 100 : 0;
        logProgress({
          variables: { courseId: id, position: pos, duration: dur, percent: pct },
        }).catch(() => {});
      }
    };
  }, [id, logProgress]);

  const handleManualOpenTest = useCallback(async () => {
    // Trigger the "ended" semantics on the server without re-firing the
    // video's native ended event, then show the modal.
    if (!id) return;
    const video = videoRef.current;
    const dur = video?.duration || progressData?.courseWatchProgress?.duration || 0;
    const pos = video?.currentTime || dur;
    try {
      await markEnded({ variables: { courseId: id, position: pos, duration: dur } });
    } catch (err) {
      // ignore
    }
    endedHandledRef.current = true;
    setTestOpen(true);
  }, [id, markEnded, progressData?.courseWatchProgress]);

  // Duration formatting
  const formatDuration = (seconds?: number | string | null) => {
    if (!seconds) return "00:00";

    if (typeof seconds === "string") {
      if (/^\d+:\d+$/.test(seconds)) return seconds;
      const num = parseInt(seconds);
      if (!isNaN(num)) seconds = num;
    }

    if (typeof seconds === "number") {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    }

    return "00:00";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-center px-4">
        <div>
          <p className="text-red-500 text-lg mb-4">
            {error ? "Алдаа гарлаа" : "Хичээл олдсонгүй"}
          </p>
          <Link to="/academy">
            <Button variant="outline">
              <ChevronLeft className="mr-2 h-4 w-4" />
              Академи руу буцах
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <Link
          to="/academy"
          className="inline-flex items-center text-primary hover:underline mb-8 text-lg font-medium"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          буцах
        </Link>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-8">
              {/* Video: embed iframe (Loom etc.) or HTML5 file */}
              {lesson.videoUrl ? (
                isEmbed ? (
                  <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
                    <iframe
                      src={resolveVideoUrl(lesson.videoUrl)}
                      title={lesson.title}
                      className="w-full h-full"
                      allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
                    <video
                      ref={videoRef}
                      src={fileUrl(lesson.videoUrl)}
                      className="w-full h-full"
                      controls
                      playsInline
                      preload="metadata"
                      onLoadedMetadata={onLoadedMetadata}
                      onTimeUpdate={onTimeUpdate}
                      onPlay={onPlay}
                      onPause={onPause}
                      onSeeking={onSeeking}
                      onEnded={handleEnded}
                    />
                  </div>
                )
              ) : (
                <div className="aspect-video bg-muted border-2 border-dashed rounded-2xl flex items-center justify-center">
                  <p className="text-muted-foreground">Видео байхгүй</p>
                </div>
              )}

              {/* Progress + completion status */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Үзсэн хувь</span>
                  <span className="font-medium">
                    {Math.round(watchedPercent)}%
                  </span>
                </div>
                <Progress value={watchedPercent} />
                {isEmbed && !completed && (
                  <div className="flex flex-wrap items-center gap-3 pt-2">
                    <Button size="sm" onClick={handleMarkEmbeddedWatched}>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Хичээл үзсэн гэж тэмдэглэх
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Видеог дуусгаад энэ товчийг дарна уу
                    </span>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {completed || alreadyPassed ? (
                    <Badge variant="default" className="bg-green-600">
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                      Дууссан
                    </Badge>
                  ) : null}
                  {test?.required ? (
                    <Badge variant="secondary">
                      Шалгалт шаардлагатай ({test.passScore}%)
                    </Badge>
                  ) : null}
                  {test && !test.required ? (
                    <Badge variant="outline">Шалгалт нэмэлт</Badge>
                  ) : null}
                  {alreadyPassed ? (
                    <Badge variant="outline" className="text-green-700 border-green-300">
                      Шалгалтанд тэнцсэн
                    </Badge>
                  ) : null}
                </div>
              </div>

              {/* Мэдээлэл */}
              <div>
                <div className="flex flex-wrap items-center gap-3 text-sm mb-4">
                  {lesson.kind && (
                    <span className="bg-primary/10 text-primary px-4 py-1.5 rounded-full font-medium">
                      {lesson.kind === "PRODUCT"
                        ? "Бүтээгдэхүүн"
                        : lesson.kind === "DEVICE"
                        ? "Багаж"
                        : "Сургалт"}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    {formatDuration(lesson.duration)}
                  </span>
                </div>

                <h1 className="text-4xl font-bold mb-4 text-foreground">
                  {lesson.title}
                </h1>

                <p
                  className="text-lg text-muted-foreground leading-relaxed"
                  dangerouslySetInnerHTML={{
                    // Pasted Loom "share to website" blocks become players.
                    __html: loomEmbedsToHtml(
                      lesson.description || "Тодорхойлолт байхгүй"
                    ),
                  }}
                />
              </div>

              {/* Test action */}
              {test && test.questions && test.questions.length > 0 && (
                <div className="rounded-lg border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="font-medium">Шалгалт өгөх</p>
                    <p className="text-sm text-muted-foreground">
                      {alreadyPassed
                        ? "Та энэ шалгалтанд тэнцсэн байна. Дуртай үедээ дахин үзээрэй."
                        : test.required
                        ? `Видео дууссаны дараа шалгалтыг амжилттай өгснөөр хичээл бүрэн дууссан гэж тооцно. (Тэнцэх: ${test.passScore}%)`
                        : `Шалгалт нэмэлт. (Тэнцэх: ${test.passScore}%)`}
                    </p>
                  </div>
                  <Button onClick={handleManualOpenTest}>
                    <PlayCircle className="h-4 w-4 mr-2" />
                    {alreadyPassed ? "Шалгалт үзэх" : "Шалгалт эхлүүлэх"}
                  </Button>
                </div>
              )}

              {/* Презентац (PPTX) */}
              {/* {lesson.presentationFile && (
                <Accordion type="single" collapsible defaultValue="presentation">
                  <AccordionItem value="presentation">
                    <AccordionTrigger className="text-xl font-semibold">
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Презентац (PPTX)
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div
                        className="pt-2 bg-white rounded-lg overflow-hidden border"
                        style={{ height: "70vh" }}
                      >
                        {pptxLoading && (
                          <div className="h-full flex items-center justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          </div>
                        )}
                        {pptxError && (
                          <div className="h-full flex items-center justify-center text-red-500 px-4 text-center">
                            Презентац ачааллахад алдаа гарлаа: {pptxError}
                          </div>
                        )}
                        {pptxContent && !pptxLoading && !pptxError && (
                          <div className="pptx-viewer-no-download h-full">
                            <PowerPointViewer
                              content={pptxContent}
                              filePath={lesson.presentationFile}
                              canEdit={false}
                              className="h-full"
                            />
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )} */}
            </div>

            <div className="lg:col-span-1" />
          </div>
        </div>
      </div>

      <CourseTestDialog
        courseId={id!}
        test={test}
        open={testOpen}
        onOpenChange={(o) => {
          setTestOpen(o);
          // Allow re-opening after the modal closes
          if (!o) {
            endedHandledRef.current = false;
            setCompleted(alreadyPassed || !test?.required);
            refetchAttempts();
          }
        }}
        onCompleted={onTestCompleted}
        alreadyPassed={alreadyPassed}
      />
    </div>
  );
};

export default VideoLessonDetail;
